import React, { useEffect, useState } from "react";
import { devDemoListPersonas, devDemoSelectPersona } from "../../api/devDemoAuth.js";
import { encrypted, tokenManager } from "../../utils/utility.jsx";
import DeepInsightsChat from "../DeepInsightsChat/DeepInsightsChat.jsx";
import "./DevDemo.css";

/**
 * Standalone persona preview page — not part of the SSO/RBAC app shell.
 * No login: a dropdown lists the known personas (see
 * app/routers/dev_demo.py), and picking one resolves straight to a synthetic
 * identity (the local-only dev-demo-<persona> token, which the backend
 * accepts via its ENABLE_DEV_DEMO-gated (or, locally, DEBUG-gated) bypass in
 * get_current_user.py). Once picked, this renders the real DeepInsightsChat
 * page/chat flow — same look and feel as the production app, just scoped to
 * a synthetic persona identity and memory key.
 */
function DevDemo() {
  const [personas, setPersonas] = useState([]);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error: listError } = await devDemoListPersonas();
      if (listError || !data) {
        setError("This preview page isn't enabled in this environment.");
        return;
      }
      setPersonas(data);
      if (data.length > 0) setSelected(data[0].persona);
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setError("");
    setLoading(true);
    const { data, error: selectError } = await devDemoSelectPersona(selected);
    setLoading(false);
    if (!data || selectError) {
      setError("Couldn't load that persona.");
      return;
    }
    const response = data;

    // Synthetic identity for useAuth()/DeepInsightsChat — token matches the
    // dev-demo-<persona> value the backend's auth bypass expects.
    tokenManager.setToken(response.token, "token");
    localStorage.setItem(
      "LoggedInDetails",
      encrypted.encrypt(
        JSON.stringify({
          user_id: response.email,
          username: response.persona,
          full_name: response.full_name,
          email: response.email,
          role: "demo",
          team: response.persona,
          ugdn_id: "DEV-DEMO",
        })
      )
    );
    localStorage.setItem("devDemoWelcomeMessage", response.welcome_message);
    localStorage.setItem(
      "devDemoExampleQuestions",
      JSON.stringify(response.example_questions || [])
    );
    setLoggedIn(true);
  };

  if (loggedIn) {
    return <DeepInsightsChat />;
  }

  return (
    <div className="dev-demo-page">
      <form className="dev-demo-card" onSubmit={handleSubmit}>
        <h2>DeepInsights — Persona Preview</h2>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={personas.length === 0}
        >
          {personas.length === 0 && <option value="">No personas available</option>}
          {personas.map((p) => (
            <option key={p.persona} value={p.persona}>
              {p.full_name}
            </option>
          ))}
        </select>
        {error && <p className="dev-demo-error">{error}</p>}
        <button type="submit" disabled={loading || !selected}>
          {loading ? "Loading…" : "View Persona"}
        </button>
      </form>
    </div>
  );
}

export default DevDemo;


